// resources/js/Components/zone/ZoneSummaryAdvanced.jsx
import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Chip,
    Collapse,
    IconButton,
    Tooltip,
    Stack,
    Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const normalizeGpioConfig = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const id = Number(value?.id);
    const name = typeof value?.name === 'string' ? value.name.trim() : '';
    const delay = Number(value?.delay ?? 0);
    const normalizeClassList = (input) => (
        Array.isArray(input)
            ? Array.from(new Set(input.map((entry) => String(entry || '').trim()).filter(Boolean)))
            : []
    );

    if (!Number.isFinite(id) || id <= 0 || !name) {
        return {};
    }

    return {
        id,
        name,
        delay: Number.isFinite(delay) && delay >= 0 ? Number(delay.toFixed(2)) : 0,
        classes: normalizeClassList(value?.classes),
        free_classes: normalizeClassList(value?.free_classes),
    };
};

const renderTextChips = (values = []) => values.map((value) => (
    <Chip
        key={value}
        label={value}
        size="small"
        variant="outlined"
        style={{ marginRight: 1, marginBottom: 1, borderRadius: 2 }}
    />
));

const isLightColor = (hexcolor) => {
    // if the color is not a valid hex color, return false
    if (!/^#[0-9A-F]{6}$/i.test(hexcolor)) {
        return false;
    }
    const r = parseInt(hexcolor.slice(1, 3), 16);
    const g = parseInt(hexcolor.slice(3, 5), 16);
    const b = parseInt(hexcolor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 127.5;
};

const renderChips = (ids, getTagById) =>
    ids.map((id) => {
        const tag = getTagById(id);
        return tag ? (
            <Chip
                key={id}
                label={tag.name}
                size="small"
                style={{
                    color: isLightColor(tag.color) ? '#000' : '#fff',
                    backgroundColor: tag.color || '#ccc',
                    marginRight: 1,
                    marginBottom: 1,
                    borderRadius: 2,
                }}
            />
        ) : null;
    });

const buildAnalyzerOptionLabels = (analyzer) => {
    const optionLabels = [];

    if (analyzer?.cumulate) {
        optionLabels.push('Cumulate');
    }
    if (analyzer?.buffering_image) {
        optionLabels.push('Buffering Image');
    }
    if (analyzer?.realtime || analyzer?.realtime_analysis) {
        optionLabels.push('Realtime');
    }
    if (analyzer?.get_images || analyzer?.get_image) {
        optionLabels.push('Get Images');
    }
    if (analyzer?.enrichment || analyzer?.enrichment_analysis) {
        optionLabels.push('Enrichment');
    }

    return optionLabels;
};

const buildDetectorSummaryText = (detector, getTagById) => {
    const sections = [];
    const classLabels = (detector?.classes || []).map((id) => getTagById(id)?.name || id).join(', ');
    const alarmLabels = (detector?.alarms || []).map((id) => getTagById(id)?.name || id).join(', ');
    const noAlarmLabels = (detector?.no_alarms || []).map((id) => getTagById(id)?.name || id).join(', ');
    const bufferingLabels = (detector?.buffering_image || []).map((id) => getTagById(id)?.name || id).join(', ');

    if (classLabels) {
        sections.push(`Classes: ${classLabels}`);
    }
    if (alarmLabels) {
        sections.push(`Alarms: ${alarmLabels}`);
    }
    if (noAlarmLabels) {
        sections.push(`No Alarms: ${noAlarmLabels}`);
    }
    if (bufferingLabels) {
        sections.push(`Buffering Image: ${bufferingLabels}`);
    }

    return `${detector?.name || '-'} (${detector?.model || '-'})${sections.length ? ` → ${sections.join(' | ')}` : ''}`;
};

const ZoneSummaryAdvanced = ({ countBasic = [], alarmsBasic = [], modelAlarms = [], getTagById, advanced = {} , advancedToggle, expandedDefault = false, markerSets = [], gpioConfig = {}, trafficLights = [] }) => {
    const [expanded, setExpanded] = useState(expandedDefault );
    const handleToggle = () => {
        setExpanded(!expanded);
    }
    const count = advanced?.model_count || [];
    const alarms = advanced?.model_alarms || [];
    const unions = Array.isArray(advanced?.union) ? advanced.union : [];
    const detectors = advanced?.detectors || [];
    const analyzers = advanced?.analyzers || [];
    const model_classes = advanced?.model_classes || [];
    const gpio = useMemo(() => normalizeGpioConfig(gpioConfig), [gpioConfig]);

    const markerSetMap = useMemo(() => {
        const lookup = new Map();
        (markerSets || []).forEach((set) => {
            const key = set?.id != null ? set.id.toString() : null;
            if (key) {
                lookup.set(key, set.name || `Marker Set ${key}`);
            }
        });
        return lookup;
    }, [markerSets]);

    const resolveMarkerSetName = (id) => {
        if (!id && id !== 0) {
            return null;
        }
        return markerSetMap.get(id.toString()) || null;
    };

    const resolveAnalyzerMarkerSetId = (analyzer) => {
        if (!analyzer) {
            return null;
        }
        return analyzer.marker_set_id ?? analyzer.marker_set ?? analyzer.markerSet?.id ?? analyzer.markerSet?.value ?? null;
    };

    const summaryText = {
        alarms: modelAlarms.map(id => getTagById(id)?.name).join(', ') || '-',
        count: count.map(id => getTagById(id)?.name).join(', ') || '-',
        model_classes: model_classes.map(id => getTagById(id)?.name).join(', ') || '-',
        unions:   unions.map(([p, c]) => `${getTagById(p)?.name || p} → ${c.map(id => getTagById(id)?.name || id).join(', ')}`).join('|\n'),
        detectors:   detectors.map((detector) => buildDetectorSummaryText(detector, getTagById)).join('|\n'),
        analyzers:   analyzers.map(a => {
            const markerLabel = resolveMarkerSetName(resolveAnalyzerMarkerSetId(a));
            const options = buildAnalyzerOptionLabels(a);
            return `${a.name} (${a.model}) → ${a.values || '-'}${markerLabel ? ` [${markerLabel}]` : ''}${options.length ? ` {${options.join(', ')}}` : ''}`;
        }).join('|\n')
    };

    return (
        <Box mt={0} sx={{ p: 0, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} sx={{borderBottom:"1px solid #ccc"}} onDoubleClick={handleToggle}>
                <Typography variant="body2" fontWeight={500}>Zone Tags Summary</Typography>
                <Tooltip title={expanded ? 'Hide Details' : 'Show Full Details'}>
                    <IconButton size="small" onClick={handleToggle}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" mb={1}>
                <Tooltip title={countBasic.map(id => getTagById(id)?.name || id).join(', ') || '-'}>
                    <Chip size="small" label={`Count: ${countBasic.length}`} />
                </Tooltip>
                <Tooltip title={alarmsBasic.map(id => getTagById(id)?.name || id).join(', ') || '-'}>
                    <Chip size="small" label={`Alarms: ${alarmsBasic.length}`} />
                </Tooltip>
                {gpio.id ? (
                    <Tooltip title={`Gate: ${gpio.name}${gpio.delay > 0 ? ` | Delay: ${gpio.delay}s` : ''}`}>
                        <Chip size="small" label={`GPIO Gate: ${gpio.name}`} />
                    </Tooltip>
                ) : null}
                {trafficLights.map((light) => <Chip key={`traffic-${light.id}`} size="small" variant="outlined" label={`Traffic light: ${light.name}`} />)}
                {advancedToggle && (<>
                <Tooltip title="Advanced model">
                        <Chip label={`Adv. Model: ${advanced.model || '-'}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.model_classes}>
                    <Chip label={`Adv. Selected Classes: ${model_classes.length}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.alarms}>
                    <Chip label={`Adv. Alarms: ${modelAlarms.length}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.count}>
                    <Chip label={`Adv. Count: ${count.length}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.unions}>
                    <Chip label={`Unions: ${unions.length}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.detectors}>
                    <Chip label={`Detectors: ${detectors.length}`} size="small" />
                </Tooltip>
                <Tooltip title={summaryText.analyzers}>
                    <Chip label={`Analyzers: ${analyzers.length}`} size="small" />
                </Tooltip>
                </>)}
            </Stack>

            <Collapse in={expanded}>
                <Box>
                    {countBasic.length > 0 && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="body2" fontWeight={500}>Count:</Typography>
                            {renderChips(countBasic, getTagById)}
                        </Box>
                    )}

                    {alarmsBasic.length > 0 && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={500}>Alarms:</Typography>
                            {renderChips(alarmsBasic, getTagById)}
                        </Box>
                    )}

                    {gpio.id ? (
                        <Box mt={2}>
                            <Typography variant="body2" fontWeight={500}>GPIO:</Typography>
                            <Box ml={2} mt={0.5}>
                                <Typography variant="body2">Gate: {gpio.name}</Typography>
                                <Typography variant="body2">Delay: {gpio.delay.toFixed(2)}s</Typography>
                                {gpio.classes.length > 0 && (
                                    <Box mt={0.5}>
                                        <Typography variant="body2">Trigger classes:</Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                            {renderTextChips(gpio.classes)}
                                        </Box>
                                    </Box>
                                )}
                                {gpio.free_classes.length > 0 && (
                                    <Box mt={0.5}>
                                        <Typography variant="body2">Free classes:</Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                            {renderTextChips(gpio.free_classes)}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
                {advancedToggle && ( <>
                <Typography variant="body2" fontWeight={500} mt={2}>Model: {advanced.model || '-'} </Typography>

                {model_classes.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2" fontWeight={500}>Adv. selected classes:</Typography>
                        {renderChips(model_classes, getTagById)}
                    </Box>
                )}

                {modelAlarms.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2" fontWeight={500}>Adv. Alarms:</Typography>
                        {renderChips(modelAlarms, getTagById)}
                    </Box>
                )}

                {count.length > 0 && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2" fontWeight={500}>Adv. Count:</Typography>
                        {renderChips(count, getTagById)}
                    </Box>
                )}

                {unions.length > 0 && (
                    <Box mt={2}>
                        <Typography variant="body2" fontWeight={500}>Unions:</Typography>
                        {unions.map(([parent, children], i) => (
                            <Box key={i} ml={2} mt={1}>
                                <Typography variant="body2" fontWeight={500}>{getTagById(parent)?.name || parent}</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                    {renderChips(children, getTagById)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {detectors.length > 0 && (
                    <Box mt={2}>
                        <Typography variant="body2" fontWeight={500}>Detectors:</Typography>
                        {detectors.map((det, i) => (
                            <Box key={i} ml={2} mt={1}>
                                <Typography variant="body2">• <strong>{det.name}</strong> (Model: {det.model}, Class: {getTagById(det.class)?.name || det.class})</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} ml={2}>
                                    {renderChips(det.classes || [], getTagById)}
                                </Box>
                                {det.alarms?.length > 0 && (
                                    <Box ml={2} mt={0.5} ><Typography variant="body2">Alarms:</Typography>{renderChips(det.alarms, getTagById)}</Box>
                                )}
                                {det.no_alarms?.length > 0 && (
                                    <Box ml={2} mt={0.5}><Typography variant="body2">No Alarms:</Typography>{renderChips(det.no_alarms, getTagById)}</Box>
                                )}
                                {det.buffering_image?.length > 0 && (
                                    <Box ml={2} mt={0.5}><Typography variant="body2">Buffering Image:</Typography>{renderChips(det.buffering_image, getTagById)}</Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {analyzers.length > 0 && (
                    <Box mt={2}>
                        <Typography variant="body2" fontWeight={500}>Analyzers:</Typography>
                        {analyzers.map((a, i) => {
                            const markerName = resolveMarkerSetName(resolveAnalyzerMarkerSetId(a));
                            const analyzerOptions = buildAnalyzerOptionLabels(a);
                            return (
                                <Box key={i} ml={2} mt={1}>
                                    <Typography variant="body2">• <strong>{a.name}</strong> (Model: {a.model}, Class: {getTagById(a.class)?.name || a.class})</Typography>
                                    {a.values && (
                                        <Box ml={2} mt={0.5}><Typography variant="body2">Alarms: {a.values}</Typography></Box>
                                    )}
                                    {markerName && (
                                        <Box ml={2} mt={0.5}><Typography variant="body2">Marker Set: {markerName}</Typography></Box>
                                    )}
                                    {analyzerOptions.length > 0 && (
                                        <Box ml={2} mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                                            {analyzerOptions.map((optionLabel) => (
                                                <Chip key={`${a.name}-${optionLabel}`} label={optionLabel} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
                </>)}
            </Collapse>
        </Box>
    );
};

export default ZoneSummaryAdvanced;
